import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { OriginLink } from '@/lib/readiness/nav'
import type { DomainDef, Evidence, IndexDef, LevelCode, ObjectiveDef, ObjectiveState, RecommendationLink, Snapshot } from '@/api/types/readiness'
import { LEVELS, levelFromNum, levelNum, transitionFor } from '@/api/types/readiness'
import { levelName, tierForScore, tiersOf, sectorBenchmark } from '@/lib/indexes/registry'
import { liveLevel, liveScore, needsConfirmation, needsScoreConfirmation, tierScore } from '@/lib/readiness/score'
import { assertScale, fmtScore } from '@/lib/readiness/scale'
import { Chip, DomainDot, Icon, ScoreBar } from '@/components/readiness/primitives'
import { ConfirmPanel, ScorePanel } from '@/components/readiness/ConfirmPanel'
import { EvidenceModal, type EvidenceInput } from '@/components/readiness/modals'
import { formatDate } from '@/components/common/format'
import { REVIEW_REASON_LABEL, levelCode, type HistoryEntry, type ReviewReason } from '@/api/clients/assessmentsApi'
import { GovernancePanel, type GovernancePanelProps } from '@/components/readiness/GovernancePanel'

// Quick view drawer (I2 §5): 480px, right-hand, over the heatmap. Read-mostly:
// it carries the single most common action (confirm a suggested level) and
// routes everything else to the objective page. Prev/Next follow the grid's
// reading order; ←/→ do the same while the drawer has focus; Escape closes.
//
// Iteration 6 (POC) §7: with no objective page, the drawer carries the two edit
// actions itself (`edit`): 640px wide, `Set level` and `+ Add evidence` in a
// sticky footer, the detail page's Set level card folding open above it, and
// the Add evidence modal. `Open objective →` is not shown.

export interface QuickViewProps {
  index: IndexDef
  domain: DomainDef
  objective: ObjectiveDef
  state: ObjectiveState | undefined
  evidence: Evidence[]
  links: RecommendationLink[]
  color: string
  theme: 'light' | 'dark'
  lastAssessment: Snapshot | null
  prev: string | null
  next: string | null
  onNavigate: (code: string) => void
  onConfirm: () => void
  onClose: () => void
  // I3 §12.2: `⚠ n step vendor-gated · earliest L{next} {date}` under Next step.
  // `of: 'dependencies'` counts the vendor components and Issuing CAs the
  // readiness service finds still quantum vulnerable instead of steps.
  gated?: { count: number; earliest: string | null; of?: 'steps' | 'dependencies' } | null
  // A served cell's Playbook plans (the readiness service's remediations for
  // its next step), in place of the in-browser `links`.
  plans?: { count: number; first: string | null } | null
  // Set when the level comes from the readiness service, which this drawer
  // cannot write to: hides Confirm and the POC edit actions and says where
  // the level is changed instead.
  readOnly?: string
  width?: number
  edit?: QuickViewEdit | null
  // A served cell that needs the Assessor: what to review and the two decisions.
  review?: QuickViewReview | null
  // A served cell's history across assessments; null while it loads.
  history?: HistoryEntry[] | null
  // CSA's verbatim statements per level (one per question), shown by "Show full CSA wording".
  csaWording?: Partial<Record<LevelCode, Array<{ label: string; text: string }>>> | null
  // Next steps the readiness service holds; they replace the built-in ones when present.
  nextSteps?: string[] | null
  // The level lives in the readiness service: the in-browser Confirm never shows.
  served?: boolean
  // GV-1 on a served cell: its two parts and the governance input form.
  governance?: GovernancePanelProps | null
}

export interface QuickViewEdit {
  manualOnly: boolean
  onSetLevel: (level: LevelCode, note: string, cause?: 'coverage' | 'posture') => void
  onSetScore: (input: { tier: number | null; score: number }, note: string) => void
  // Absent when the level's home cannot take evidence from here (a served cell).
  onAddEvidence?: (input: EvidenceInput) => void
  // A note is required for every level set, not only for an override.
  requireNote?: boolean
  busy?: boolean
  error?: string | null
}

export interface QuickViewReview {
  reason: ReviewReason
  current: LevelCode | null
  suggested: LevelCode | null
  suggestionReason: string | null
  source: 'discovery' | 'playbook' | null
  // Where a carried-forward level came from.
  previousAssessment: string | null
  onAccept: (note: string) => void
  onKeep: (note: string) => void
  busy: boolean
  error: string | null
}

const MODULE_LABEL = { discovery: 'Discovery', playbook: 'Playbook' } as const

// One history line in plain words, e.g. "L1 → L2 set by ana@… : reason".
function historyTitle(h: HistoryEntry): string {
  const from = levelCode(h.from_level)
  const to = levelCode(h.to_level)
  switch (h.kind) {
    case 'level_set':
      return from && from !== to ? `${from} → ${to}` : `${to} set`
    case 'accepted':
      return `Suggested ${to} accepted${from && from !== to ? ` (was ${from})` : ''}`
    case 'kept':
      return `${to} confirmed`
    case 'suggested':
      return to ? `${h.source ? MODULE_LABEL[h.source] : 'Sync'} suggests ${to}` : `${h.source ? MODULE_LABEL[h.source] : 'Sync'} suggestion withdrawn`
    case 'input_updated':
      return 'Governance input updated'
  }
}

// Break a descriptor into lettered pointers (a, b, …) on sentence boundaries.
function pointers(text: string): string[] {
  const parts = text
    .split(/(?<=[.;])\s+(?=[A-Z])|,\s+and\s+|;\s+/)
    .map((s) => s.trim().replace(/[.;]$/, ''))
    .filter(Boolean)
  return parts.length ? parts : [text]
}
const letter = (i: number) => String.fromCharCode(97 + i)

export function QuickViewDrawer(p: QuickViewProps) {
  const { index, domain, objective, state, evidence, links, color, theme, lastAssessment, prev, next, onNavigate, onConfirm, onClose, gated, plans, readOnly, width = 480 } = p
  // A level served by the readiness service is changed in the assessment, not
  // here: the POC edit actions stand down, but the objective link stays hidden.
  const edit = readOnly ? null : p.edit
  const hasObjectivePage = !p.edit
  const scale = assertScale(index)
  const ref = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [full, setFull] = useState(false)
  const [allSteps, setAllSteps] = useState(false)
  const [keeping, setKeeping] = useState(false)
  const [reviewNote, setReviewNote] = useState('')
  const [panelOpen, setPanelOpen] = useState(false)
  const [adding, setAdding] = useState(false)
  const addingRef = useRef(false)
  addingRef.current = adding
  useEffect(() => {
    ref.current?.focus()
  }, [objective.code])
  useEffect(() => {
    setFull(false)
    setAllSteps(false)
    setKeeping(false)
    setReviewNote('')
    setPanelOpen(false)
  }, [objective.code])
  // A decision made closes the note it was typed in.
  const review = p.review ?? null
  const reviewKey = review ? `${review.reason}:${review.current}:${review.suggested}` : ''
  useEffect(() => {
    setKeeping(false)
    setReviewNote('')
  }, [reviewKey])
  // Opening the Set level panel scrolls it into view and focuses its first level button.
  useEffect(() => {
    if (!panelOpen) return
    const el = panelRef.current
    const body = el?.parentElement
    // Scroll the drawer body only; scrollIntoView would also move the page behind it.
    if (el && body) body.scrollTo({ top: el.offsetTop + el.offsetHeight - body.clientHeight + 16, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
    el?.querySelector<HTMLElement>('[role="radio"]')?.focus({ preventScroll: true })
  }, [panelOpen])
  // Escape closes from anywhere (focus may leave the drawer when the confirm
  // button disappears after use); ←/→ only while the drawer has focus. The
  // Add evidence modal takes its own Escape.
  useEffect(() => {
    const onDoc = (e: KeyboardEvent) => e.key === 'Escape' && !addingRef.current && onClose()
    document.addEventListener('keydown', onDoc)
    return () => document.removeEventListener('keydown', onDoc)
  }, [onClose])
  const onKey = (e: React.KeyboardEvent) => {
    // Arrow keys inside a field move the caret, not the objective.
    if ((e.target as HTMLElement).closest('input, textarea, select')) return
    if (e.key === 'ArrowLeft' && prev) onNavigate(prev)
    else if (e.key === 'ArrowRight' && next) onNavigate(next)
  }
  const unset = !state || state.status === 'unset'
  const level = liveLevel(state)
  const score = liveScore(state)
  const n = levelNum(level)
  const nextLevel = level === 'L3' ? null : levelFromNum(n + 1)
  const pending = scale === 'levels' ? needsConfirmation(state) : needsScoreConfirmation(state)
  const suggested = state?.status === 'suggested'
  const prevItem = lastAssessment?.items.find((i) => i.objective_code === objective.code)
  const bullets = p.nextSteps?.length ? p.nextSteps : scale === 'levels' ? (objective.recommendations[transitionFor(level)] ?? []) : (objective.actions ?? []).map((a) => a.text)
  const firstLink = links.find((l) => l.bullet_index === 0)
  // Under the next step: its Playbook plans (a served objective with none says
  // nothing about plans) or linked action, and how many steps follow.
  const nextMeta = [
    plans
      ? plans.count ? `${plans.count} Playbook ${plans.count === 1 ? 'plan' : 'plans'} in progress${plans.first ? ` · ${plans.first}` : ''}` : null
      : firstLink ? `Playbook action ${firstLink.status.replace('_', ' ')} · ${firstLink.action_title}` : 'Not yet linked to a Playbook action',
  ].filter(Boolean).join(' · ')
  const moreSteps = bullets.length - 1
  const sourceOf = (e: Evidence) => {
    const s = e.source === 'discovery' ? { label: 'Discovery', tone: 'ok' as const } : e.source === 'playbook' ? { label: 'Playbook', tone: 'info' as const } : { label: 'File', tone: 'neutral' as const }
    return e.source_label ? { ...s, label: e.source_label } : s
  }
  const q = `?index=${index.id}`
  const statusChip = unset ? (
    <Chip tone="dashed">Not set</Chip>
  ) : suggested || pending ? (
    <Chip tone="warn">Suggested · not confirmed</Chip>
  ) : (
    <Chip tone="ok">Confirmed {formatDate(state?.set_at)}</Chip>
  )

  // "Why L{n}" paragraph (I1 §8.3), referencing pointer letters (I2 §5.1).
  const why = (() => {
    if (unset) return 'No level has been set yet. Add evidence and declare a level, or run a sync.'
    if (scale === 'score_0_10') {
      const t = tierForScore(index, objective.code, score)
      return `${suggested ? 'Quantizant suggests' : `${state?.set_by} set`} ${fmtScore(score)}${t ? `, matching tier ${t.n}: "${t.statement}"` : ''}. ${state?.suggested_reason ?? ''}`.trim()
    }
    const cur = scale === 'levels' ? pointers(objective.levels[level]) : []
    const refs = cur.map((_, i) => `${level}(${letter(i)})`)
    const matches = refs.length > 1 ? `matches ${refs.slice(0, -1).join(', ')} and ${refs[refs.length - 1]}` : `matches ${refs[0] ?? level}`
    if (suggested) return `Quantizant suggests ${level}: ${state?.suggested_reason ?? ''} The evidence ${matches}.`
    const base = `Set by ${state?.set_by} on ${formatDate(state?.set_at)}${state?.note ? `: ${state.note.replace(/[.\s]+$/, '')}.` : '.'} The evidence ${matches}.`
    return pending && state?.suggested_level ? `${base} The latest sync suggests ${state.suggested_level}: ${state.suggested_reason ?? ''}` : base
  })()

  const prevNext = (
    <span className="flex shrink-0 items-center gap-1">
      <button type="button" className="rd-btn rd-btn-sm" disabled={!prev} onClick={() => prev && onNavigate(prev)} aria-label={prev ? `Previous · ${prev}` : 'No previous objective'} data-testid="quickview-prev">
        <Icon name="chevron-left" size={13} /> Prev{prev ? ` · ${prev}` : ''}
      </button>
      <button type="button" className="rd-btn rd-btn-sm" disabled={!next} onClick={() => next && onNavigate(next)} aria-label={next ? `Next · ${next}` : 'No next objective'} data-testid="quickview-next">
        Next{next ? ` · ${next}` : ''} <Icon name="chevron-right" size={13} />
      </button>
    </span>
  )

  return createPortal(
    <div className="rd fixed inset-0 z-[55]" data-testid="quick-view">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div
        ref={ref}
        role="dialog"
        aria-modal="false"
        aria-label={`Quick view · ${objective.code}`}
        tabIndex={-1}
        onKeyDown={onKey}
        className="absolute right-0 top-0 flex h-full w-full flex-col overflow-hidden outline-none"
        style={{ maxWidth: width, background: 'var(--rd-bg-header)', borderLeft: '1px solid var(--rd-border-control)', boxShadow: 'var(--rd-shadow-float)' }}
      >
        {/* Header (tinted) */}
        <div className="px-5 pb-4 pt-4" style={{ background: `${color}14`, borderBottom: '1px solid var(--rd-border-subtle)' }}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="flex flex-wrap items-center gap-2 text-[11.5px] font-semibold uppercase tracking-wider" style={{ color }}>
                <DomainDot domain={domain} theme={theme} size={8} /> {domain.name} · {scale === 'levels' ? 'Objective' : 'Sub-index'} {domain.objectives.findIndex((o) => o.code === objective.code) + 1}
                {objective.no_regrets && <Chip tone="chip">NO-REGRETS</Chip>}
              </p>
              <h2 className="mt-1 text-[18px] font-bold leading-snug">{objective.title}</h2>
              <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>
                {lastAssessment ? `Previous ${scale === 'levels' ? (prevItem?.level ?? 'L0') : fmtScore(prevItem?.score ?? null)} · ${lastAssessment.name}` : 'No earlier assessment'}
              </p>
            </div>
            <button type="button" onClick={onClose} className="rounded-lg p-1" style={{ color: 'var(--rd-text-muted)' }} aria-label="Close quick view">
              <Icon name="x" size={18} />
            </button>
          </div>
        </div>

        <div className="relative min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {/* Level ladder / tier ladder */}
          <section>
            <div className="flex items-center justify-between">
              <p className="rd-label">{scale === 'levels' ? 'Level' : 'Score'}</p>
              {statusChip}
            </div>
            {scale === 'levels' ? (
              <div className="mt-2 space-y-1.5">
                {LEVELS.map((l) => {
                  const i = levelNum(l)
                  const isCur = !unset && l === level
                  const isNext = nextLevel === l || (unset && l === 'L0')
                  const text = objective.levels[l]
                  if (!full && !isCur && !isNext) {
                    return (
                      <div key={l} className="flex items-center gap-2 px-2 py-1 text-[12.5px]" style={{ color: 'var(--rd-text-faint)' }}>
                        <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: `var(--rd-level-${l.toLowerCase()})` }} aria-hidden="true" />
                        <span className="rd-mono">{l}</span>
                        <span className="min-w-0 flex-1 truncate">{levelName(index, l)} · {text.slice(0, 60)}{text.length > 60 ? '…' : ''}</span>
                        {i < n && !unset && <span style={{ color: 'var(--rd-status-ok-text)' }}>✓ passed</span>}
                      </div>
                    )
                  }
                  return (
                    <div key={l} className="rounded-[10px] p-3" style={isCur ? { border: `2px solid ${color}`, background: `${color}14` } : { border: '1px solid var(--rd-border-control)' }}>
                      <div className="flex items-center justify-between">
                        <p className="text-[14px] font-bold"><span className="rd-mono">{l}</span> {levelName(index, l)}</p>
                        {isCur && <span className="rd-chip" style={{ background: color, color: 'var(--accent-ink)', fontWeight: 700 }}>CURRENT</span>}
                        {!isCur && isNext && !unset && <span className="text-[11px] font-bold" style={{ color: 'var(--rd-accent-link)' }}>NEXT</span>}
                      </div>
                      {full && p.csaWording?.[l]?.length ? (
                        <ul className="mt-1.5 space-y-1.5 text-[12.5px] leading-relaxed" style={{ color: isCur ? 'var(--rd-text-secondary)' : 'var(--rd-text-muted)' }} data-testid={`csa-wording-${l}`}>
                          {p.csaWording[l]!.map((st, k) => (
                            <li key={k}>
                              {st.label && <span className="rd-label block !text-[10.5px]">{st.label}</span>}
                              <span>{st.text}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <ol className="mt-1.5 space-y-1 text-[12.5px] leading-relaxed" style={{ color: isCur ? 'var(--rd-text-secondary)' : 'var(--rd-text-muted)' }}>
                          {(full ? [text] : pointers(text)).map((pt, k) => (
                            <li key={k} className="flex gap-2"><span className="rd-mono shrink-0" style={{ color: 'var(--rd-text-faint)' }}>{full ? '' : `${letter(k)}.`}</span><span>{pt}</span></li>
                          ))}
                        </ol>
                      )}
                    </div>
                  )
                })}
                <button type="button" className="rd-link text-[12.5px]" onClick={() => setFull((v) => !v)}>{full ? 'Show pointers' : 'Show full CSA wording'}</button>
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                <div className="flex items-baseline gap-3">
                  <span className="rd-mono text-[28px] font-extrabold" style={{ color: unset ? 'var(--rd-text-muted)' : color }}>{unset ? '—' : fmtScore(score)}</span>
                  {!unset && tierForScore(index, objective.code, score) && <Chip tone="tile">TIER {tierForScore(index, objective.code, score)!.n} OF {tiersOf(index, objective.code).length}</Chip>}
                </div>
                <ScoreBar score={score} color={color} sector={sectorBenchmark(index, objective.code)} suggested={suggested} />
                <ol className="space-y-1.5">
                  {tiersOf(index, objective.code).map((t) => {
                    const cur = tierForScore(index, objective.code, score)?.n === t.n && !unset
                    return (
                      <li key={t.n} className="rounded-[10px] p-2.5 text-[12.5px] leading-relaxed" style={cur ? { border: `2px solid ${color}`, background: `${color}14` } : { border: '1px solid var(--rd-border-control)', color: 'var(--rd-text-muted)' }}>
                        <span className="rd-mono font-semibold" style={{ color: 'var(--rd-text-secondary)' }}>Tier {t.n} · {tierScore(t.n, tiersOf(index, objective.code).length).toFixed(1)}</span> {t.statement}
                        <span className="block text-[11px]" style={{ color: 'var(--rd-text-faint)' }}>{t.source_page}</span>
                      </li>
                    )
                  })}
                </ol>
              </div>
            )}
          </section>

          {review && (
            <section className="rounded-[10px] p-3" style={{ border: '1px solid var(--rd-status-warn)', background: 'color-mix(in srgb, var(--rd-status-warn) 8%, transparent)' }} data-testid="quickview-review">
              <p className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: 'var(--rd-status-warn)' }}>
                <Icon name="warn" size={14} /> Needs your review · {REVIEW_REASON_LABEL[review.reason]}
              </p>
              <p className="mt-1.5 text-[12.5px] leading-relaxed" style={{ color: 'var(--rd-text-secondary)' }}>
                {review.reason === 'suggestion' ? (
                  <>
                    {review.source ? MODULE_LABEL[review.source] : 'The latest sync'} now points to <b>{review.suggested}</b>
                    {review.current ? <> (your level: <b>{review.current}</b>)</> : null}. {review.suggestionReason}{' '}
                    {review.current ? `${review.current} stays until you decide.` : 'Nothing is set until you accept it or set a level.'}
                  </>
                ) : review.reason === 'carried_forward' ? (
                  <>{review.current} was carried over{review.previousAssessment ? ` from ${review.previousAssessment}` : ' from the last assessment'}. Confirm it still holds, or set a different level.</>
                ) : (
                  <>{review.current} has no evidence yet. Add evidence that supports it, or set a lower level.</>
                )}
              </p>
              {review.reason !== 'no_evidence' && (
                keeping ? (
                  <div className="mt-2">
                    <label htmlFor="rd-review-note" className="rd-label block">Why {review.current} stays {review.reason === 'suggestion' ? '(required)' : '(optional)'}</label>
                    <textarea id="rd-review-note" className="rd-input mt-1" rows={2} value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} placeholder={review.reason === 'suggestion' ? `e.g. the evidence still supports ${review.current}` : 'Optional context for auditors'} data-testid="quickview-review-note" />
                    <div className="mt-2 flex justify-end gap-2">
                      <button type="button" className="rd-btn" onClick={() => setKeeping(false)} disabled={review.busy}>Cancel</button>
                      <button type="button" className="rd-btn rd-btn-primary" disabled={review.busy || (review.reason === 'suggestion' && !reviewNote.trim())} onClick={() => review.onKeep(reviewNote.trim())} data-testid="quickview-keep-confirm">
                        {review.reason === 'suggestion' ? `Keep ${review.current}` : `Confirm ${review.current}`}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 flex flex-wrap justify-end gap-2">
                    {review.current && (
                      <button type="button" className="rd-btn" disabled={review.busy} onClick={() => setKeeping(true)} data-testid="quickview-keep">
                        {review.reason === 'suggestion' ? `Keep ${review.current}` : `Confirm ${review.current}`}
                      </button>
                    )}
                    {review.reason === 'suggestion' && review.suggested && (
                      <button type="button" className="rd-btn rd-btn-primary" disabled={review.busy} onClick={() => review.onAccept('')} data-testid="quickview-accept">
                        <Icon name="check" size={14} /> Accept {review.suggested}
                      </button>
                    )}
                  </div>
                )
              )}
              {review.error && <p className="mt-2 text-[12px]" style={{ color: 'var(--rd-status-danger, var(--rd-status-warn))' }} role="alert">{review.error}</p>}
            </section>
          )}

          <section>
            <p className="rd-label">Why {unset ? 'not set' : scale === 'levels' ? level : fmtScore(score)}</p>
            <p className="mt-1 text-[12.5px] leading-relaxed" style={{ color: 'var(--rd-text-secondary)' }}>{why}</p>
          </section>

          {p.governance && <GovernancePanel {...p.governance} />}

          <section>
            <p className="rd-label">Evidence ({evidence.length})</p>
            {evidence.length === 0 ? (
              <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>No evidence yet.</p>
            ) : (
              <ul className="mt-2 space-y-1.5">
                {evidence.map((e) => {
                  const s = sourceOf(e)
                  return (
                    <li key={e.id} className="flex items-center gap-2 rounded-[8px] px-2.5 py-2 text-[12.5px]" style={{ background: 'var(--rd-bg-card)', border: '1px solid var(--rd-border-subtle)' }}>
                      <Icon name={e.source === 'manual' ? 'file' : e.source === 'playbook' ? 'link' : 'check'} size={13} style={{ color: 'var(--rd-text-muted)' }} />
                      <span className="min-w-0 flex-1 truncate">{e.title} <span style={{ color: 'var(--rd-text-faint)' }}>· {formatDate(e.occurred_at)}</span></span>
                      {edit && e.link?.startsWith('http') && <a href={e.link} target="_blank" rel="noreferrer" className="rd-link shrink-0">Open ↗</a>}
                      <Chip tone={s.tone}>{s.label}</Chip>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          {bullets.length > 0 && (
            <section>
              <p className="rd-label">{scale === 'levels' ? (nextLevel ? `Next step to ${nextLevel}` : 'Sustain') : 'Next step'}</p>
              {allSteps ? (
                <ol className="mt-1 list-decimal space-y-1 pl-5 text-[12.5px] leading-relaxed" data-testid="quickview-all-steps">
                  {bullets.map((b, i) => <li key={i}>{b}</li>)}
                </ol>
              ) : (
                <p className="mt-1 text-[12.5px] leading-relaxed">{bullets[0]}</p>
              )}
              {(nextMeta || moreSteps > 0) && (
                <p className="mt-1 text-[11.5px]" style={{ color: 'var(--rd-text-muted)' }}>
                  {nextMeta}
                  {nextMeta && moreSteps > 0 ? ' · ' : ''}
                  {moreSteps > 0 && (
                    <button type="button" className="rd-link" onClick={() => setAllSteps((v) => !v)} aria-expanded={allSteps} data-testid="quickview-more-steps">
                      {allSteps ? 'Show first step only' : `${moreSteps} more ${moreSteps === 1 ? 'step' : 'steps'}`}
                    </button>
                  )}
                </p>
              )}
              {gated && gated.count > 0 && <p className="mt-1 text-[11.5px]" style={{ color: 'var(--rd-status-warn)' }} data-testid="quickview-gated">⚠ {gated.of === 'dependencies' ? `${gated.count} vendor ${gated.count === 1 ? 'dependency' : 'dependencies'} still quantum-vulnerable` : `${gated.count} step vendor-gated`}{nextLevel && gated.earliest ? ` · earliest ${nextLevel} ${new Date(gated.earliest).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}` : ''}</p>}
            </section>
          )}

          {p.history !== undefined && (
            <section data-testid="quickview-history">
              <p className="rd-label">History</p>
              {p.history === null ? (
                <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>Loading…</p>
              ) : p.history.length === 0 ? (
                <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>No history yet.</p>
              ) : (
                <ol className="mt-2 space-y-2">
                  {p.history.map((h, i) => (
                    <li key={`${h.id}-${i}`} className="flex gap-2.5 text-[12.5px]">
                      <span className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: h.kind === 'suggested' ? 'var(--rd-status-info)' : h.kind === 'input_updated' ? 'var(--rd-text-faint)' : 'var(--rd-status-ok)' }} aria-hidden="true" />
                      <span className="min-w-0 flex-1">
                        <span className="font-semibold">{historyTitle(h)}</span>
                        <span style={{ color: 'var(--rd-text-faint)' }}> · {formatDate(h.occurred_at)} · {h.assessment.title}</span>
                        <span className="block" style={{ color: 'var(--rd-text-muted)' }}>
                          {h.actor ? `By ${h.actor.display_name}` : `By ${h.source ? MODULE_LABEL[h.source] : 'sync'}`}
                          {h.note ? ` · ${h.note}` : ''}
                        </span>
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          )}

          {((suggested && !review && !p.served) || readOnly || hasObjectivePage) && (
            <section className="space-y-2">
              {readOnly && <p className="text-[12px]" style={{ color: 'var(--rd-text-muted)' }} data-testid="quickview-readonly">{readOnly}</p>}
              {suggested && !readOnly && !review && !p.served && (
                <button type="button" className="rd-btn rd-btn-primary w-full" onClick={onConfirm} data-testid="quickview-confirm">
                  <Icon name="check" size={14} /> Confirm suggested {scale === 'levels' ? state?.suggested_level : fmtScore(state?.suggested_score ?? null)}
                </button>
              )}
              {hasObjectivePage && (
                <>
                  <OriginLink to={`/readiness/${objective.code}${q}`} className="rd-btn w-full" data-testid="quickview-open">Open objective →</OriginLink>
                  <p className="text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}>Override with a note, add evidence or assign next steps on the objective page.</p>
                </>
              )}
            </section>
          )}

          {/* Set level (POC §7.3): the detail page's card, folded open from the footer. */}
          {edit && panelOpen && (
            <div ref={panelRef} className="relative" data-testid="quickview-set-level-panel">
              <button type="button" className="rd-link absolute right-5 top-5 z-[1] text-[12.5px]" onClick={() => setPanelOpen(false)} aria-label="Collapse Set level" data-testid="quickview-collapse">Collapse ⌃</button>
              {scale === 'levels' ? (
                <ConfirmPanel index={index} state={state} evidenceCount={evidence.length} color={color} requireNote={edit.requireNote} embedded={{ onCancel: () => setPanelOpen(false) }} onConfirm={(l, note, cause) => { edit.onSetLevel(l, note, cause); setPanelOpen(false) }} />
              ) : (
                <ScorePanel index={index} code={objective.code} state={state} evidenceCount={evidence.length} color={color} embedded={{ onCancel: () => setPanelOpen(false) }} onConfirm={(input, note) => { edit.onSetScore(input, note); setPanelOpen(false) }} />
              )}
            </div>
          )}
        </div>

        {edit?.error && <p className="px-5 py-2 text-[12px]" style={{ color: 'var(--rd-status-warn)', borderTop: '1px solid var(--rd-border-subtle)' }} role="alert">{edit.error}</p>}
        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-5 py-3 text-[12.5px]" style={{ borderTop: '1px solid var(--rd-border-subtle)', color: 'var(--rd-text-muted)', background: edit ? 'var(--rd-bg-header)' : undefined }} data-testid="quickview-footer">
          <span className="min-w-0 truncate">
            {unset ? 'Not set' : suggested && readOnly ? `Awaiting confirmation · ${formatDate(state?.suggested_at)}` : suggested ? `Suggested by sync · ${formatDate(state?.suggested_at)}` : `${state?.status === 'confirmed' ? 'Confirmed' : 'Set'} by ${state?.set_by ?? '—'} · ${formatDate(state?.set_at)}`}
          </span>
          {edit ? (
            <span className="flex shrink-0 items-center gap-2">
              <button type="button" className="rd-btn" onClick={() => setPanelOpen((v) => !v)} aria-expanded={panelOpen} aria-pressed={panelOpen} style={panelOpen ? { background: 'var(--rd-bg-tile)', borderColor: 'var(--rd-accent-link)' } : undefined} data-testid="quickview-set-level">
                {scale === 'levels' ? 'Set level' : 'Set score'}
              </button>
              {edit.onAddEvidence && (
                <button type="button" className="rd-btn rd-btn-primary" onClick={() => setAdding(true)} data-testid="quickview-add-evidence">
                  <Icon name="plus" size={14} /> Add evidence
                </button>
              )}
            </span>
          ) : prevNext}
        </div>
        {edit && (
          <div className="flex items-center justify-end gap-1 px-5 py-2" style={{ borderTop: '1px solid var(--rd-border-subtle)' }} data-testid="quickview-prevnext">
            {prevNext}
          </div>
        )}
      </div>
      {edit && adding && (
        <EvidenceModal
          index={index}
          objectiveCode={objective.code}
          manualOnly={edit.manualOnly}
          onClose={() => setAdding(false)}
          onAdd={(input) => {
            edit.onAddEvidence?.(input)
            setAdding(false)
          }}
        />
      )}
    </div>,
    document.body
  )
}

